import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PortableText } from '@portabletext/react';
import { motion } from 'framer-motion';
import client, { urlFor } from './sanityClient';
import Navbar from './Navbar';
import Footer from './Footer';

function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client
      .fetch(`*[_type == "post" && slug.current == $slug][0] {
        title, metaTitle, metaDescription, primaryKeyword,
        mainImage, publishedAt, excerpt, body, faqs
      }`, { slug })
      .then((data) => { setPost(data); setLoading(false); });
  }, [slug]);

  if (loading) return (
    <section>
      <Navbar />
      <div style={styles.loading}>
        <i className="fa fa-paw" style={styles.loadingPaw}></i>
        <p>Loading...</p>
      </div>
      <Footer />
    </section>
  );

  if (!post) return (
    <section>
      <Navbar />
      <div style={styles.loading}>Post not found.</div>
      <Footer />
    </section>
  );

  const postUrl = `https://www.furbabies.ai/blog/${slug}`;
  const seoTitle = post.metaTitle || post.title;
  const seoDesc = post.metaDescription || post.excerpt;

  const portableTextComponents = {
    types: {
      pullQuote: ({ value }) => (
        <motion.blockquote style={styles.pullQuote}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <i className="fa fa-paw" style={styles.pullQuotePaw}></i>
          <p style={styles.pullQuoteText}>"{value.quote}"</p>
        </motion.blockquote>
      ),
      image: ({ value }) => (
        <figure style={styles.figure}>
          <img src={urlFor(value).width(700).url()} alt={value.alt || post.title} style={styles.inlineImage} loading="lazy" />
          {value.caption && <figcaption style={styles.caption}>{value.caption}</figcaption>}
        </figure>
      ),
      ctaBlock: ({ value }) => (
        <div style={styles.inlineCta}>
          <p style={styles.inlineCtaText}>{value.text}</p>
          {value.buttonText && value.buttonLink && (
            <Link to={value.buttonLink} style={styles.inlineCtaBtn}>{value.buttonText} →</Link>
          )}
        </div>
      ),
    },
    block: {
      h2: ({ children }) => <h2 style={styles.h2}>{children}</h2>,
      h3: ({ children }) => <h3 style={styles.h3}>{children}</h3>,
      h4: ({ children }) => <h4 style={styles.h4}>{children}</h4>,
      normal: ({ children }) => <p style={styles.paragraph}>{children}</p>,
    },
    list: {
      bullet: ({ children }) => <ul style={styles.ul}>{children}</ul>,
      number: ({ children }) => <ol style={styles.ol}>{children}</ol>,
    },
    listItem: {
      bullet: ({ children }) => <li style={styles.li}>{children}</li>,
      number: ({ children }) => <li style={styles.li}>{children}</li>,
    },
    marks: {
      strong: ({ children }) => <strong style={styles.strong}>{children}</strong>,
      em: ({ children }) => <em style={{fontStyle:'italic'}}>{children}</em>,
    }
  };

  return (
    <section>
      <Helmet>
        <title>{seoTitle} – Furbabies.AI</title>
        <meta name="description" content={seoDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:url" content={postUrl} />
        {post.mainImage && <meta property="og:image" content={urlFor(post.mainImage).width(1200).height(630).url()} />}
        <link rel="canonical" href={postUrl} />
      </Helmet>

      <Navbar />

      {post.mainImage && (
        <div style={styles.coverWrapper}>
          <img src={urlFor(post.mainImage).width(1400).height(500).url()} alt={post.title} style={styles.coverImage} width="1400" height="500" />
          <div style={styles.coverOverlay} />
        </div>
      )}

      <div style={styles.container}>
        <Link to="/blog" style={styles.back}>← Back to Blog</Link>

        {post.primaryKeyword && <span style={styles.tag}>{post.primaryKeyword}</span>}

        <motion.h1 style={styles.title} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          {post.title}
        </motion.h1>

        <p style={styles.date}>
          <i className="fa fa-paw" style={{color: '#bda697', marginRight: '8px', fontSize: '0.8rem'}}></i>
          {new Date(post.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>

        <div style={styles.divider} />

        <div style={styles.content}>
          <PortableText value={post.body} components={portableTextComponents} />
        </div>

        <div style={styles.divider} />

        {post.faqs && post.faqs.length > 0 && (
          <div style={styles.faqContainer}>
            <h2 style={styles.faqTitle}>
              <i className="fa fa-paw" style={{color: '#bda697', marginRight: '12px'}}></i>
              Frequently Asked Questions
            </h2>
            {post.faqs.map((faq, index) => (
              <motion.div key={index} style={styles.faqItem}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <h3 style={styles.faqQuestion}>{faq.question}</h3>
                <p style={styles.faqAnswer}>{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        )}

        <div style={styles.shareContainer}>
          <p style={styles.shareTitle}>
            <i className="fa fa-paw" style={{color: '#bda697', marginRight: '8px'}}></i>
            Found this helpful? Share it
          </p>
          <div style={styles.shareButtons}>
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(postUrl)}`} target="_blank" rel="noopener noreferrer" style={{...styles.shareBtn, background: '#000'}}>Share on X</a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`} target="_blank" rel="noopener noreferrer" style={{...styles.shareBtn, background: '#BDA697'}}>Share on LinkedIn</a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`} target="_blank" rel="noopener noreferrer" style={{...styles.shareBtn, background: '#BDA697'}}>Share on Facebook</a>
          </div>
        </div>
      </div>

      <section style={styles.ctaSection}>
        <motion.h2 style={styles.ctaSectionTitle} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          Keep Your Furbaby Safe & Healthy
        </motion.h2>
        <p style={styles.ctaSectionText}>
          Join Us Today and Discover a World of Happiness and Health for Your Pet.
        </p>
        <Link to="/signup" style={styles.ctaButton}>Sign Up for Free</Link>
      </section>

      <Footer />
    </section>
  );
}

const styles = {
  loading: { textAlign: 'center', padding: '120px 20px', fontFamily: "'Poppins', sans-serif", fontSize: '1.2rem', color: '#967d6c', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  loadingPaw: { fontSize: '3rem', color: '#bda697', marginBottom: '16px', display: 'block' },
  coverWrapper: { position: 'relative', width: '100%', height: '420px', overflow: 'hidden' },
  coverImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  coverOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%', background: 'linear-gradient(to bottom, transparent, white)' },
  container: { maxWidth: '760px', margin: '0 auto',marginTop:"10rem", padding: '40px 20px 60px' },
  back: { fontFamily: "'Poppins', sans-serif", color: '#967d6c', textDecoration: 'none', fontWeight: '700', display: 'inline-block', marginBottom: '20px', fontSize: '0.95rem' },
  tag: { display: 'inline-block', background: '#e8e1da', color: '#967d6c', padding: '4px 14px',marginLeft:"1rem", borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', fontFamily: "'Poppins', sans-serif", marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  title: { fontFamily: "'Poltawski Nowy', serif", fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 'bold', color: '#222222', lineHeight: '1.2', marginBottom: '16px' },
  date: { fontFamily: "'Poppins', sans-serif", color: '#999', fontSize: '0.9rem', marginBottom: '32px' },
  divider: { height: '1px', background: '#e8e1da', margin: '40px 0' },
  content: { marginBottom: '40px' },
  paragraph: { fontFamily: "'Poppins', sans-serif", fontSize: '1.05rem', lineHeight: '1.9', color: '#222222', opacity: 0.85, marginBottom: '20px' },
  h2: { fontFamily: "'Poltawski Nowy', serif", fontSize: '1.8rem', fontWeight: 'bold', color: '#222222', marginTop: '48px', marginBottom: '16px', lineHeight: '1.3' },
  h3: { fontFamily: "'Poltawski Nowy', serif", fontSize: '1.4rem', fontWeight: 'bold', color: '#222222', marginTop: '36px', marginBottom: '12px' },
  h4: { fontFamily: "'Poppins', sans-serif", fontSize: '1.1rem', fontWeight: '700', color: '#222222', marginTop: '24px', marginBottom: '8px' },
  strong: { fontWeight: '700', color: '#222222' },
  ul: { paddingLeft: '24px', marginBottom: '20px' },
  ol: { paddingLeft: '24px', marginBottom: '20px' },
  li: { fontFamily: "'Poppins', sans-serif", fontSize: '1.05rem', lineHeight: '1.9', color: '#222222', opacity: 0.85, marginBottom: '10px' },
  pullQuote: { borderLeft: '4px solid #967d6c', margin: '40px 0', padding: '20px 28px', background: '#e8e1da', borderRadius: '0 12px 12px 0' },
  pullQuotePaw: { color: '#bda697', fontSize: '1.2rem', marginBottom: '8px', display: 'block' },
  pullQuoteText: { fontFamily: "'Poltawski Nowy', serif", fontSize: '1.25rem', fontStyle: 'italic', color: '#967d6c', lineHeight: '1.7', margin: 0, fontWeight: '700' },
  figure: { margin: '36px 0' },
  inlineImage: { width: '100%', borderRadius: '12px' },
  caption: { textAlign: 'center', fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem', color: '#999', marginTop: '10px' },
  inlineCta: { background: '#e8e1da', borderRadius: '12px', padding: '24px 28px', margin: '36px 0', textAlign: 'center' },
  inlineCtaText: { fontFamily: "'Poltawski Nowy', serif", fontSize: '1.2rem', color: '#222222', marginBottom: '16px', fontWeight: '700' },
  inlineCtaBtn: { display: 'inline-block', background: '#967d6c', color: 'white', padding: '12px 32px', borderRadius: '9999px', fontFamily: "'Poppins', sans-serif", fontWeight: 'bold', textDecoration: 'none', fontSize: '0.95rem' },
  faqContainer: { background: '#e8e1da', borderRadius: '16px', padding: '36px', marginBottom: '48px' },
  faqTitle: { fontFamily: "'Poltawski Nowy', serif", fontSize: '1.6rem', fontWeight: 'bold', color: '#222222', marginBottom: '28px' },
  faqItem: { marginBottom: '28px', borderBottom: '1px solid #bda697', paddingBottom: '28px' },
  faqQuestion: { fontFamily: "'Poltawski Nowy', serif", fontSize: '1.05rem', fontWeight: 'bold', color: '#222222', marginBottom: '10px' },
  faqAnswer: { fontFamily: "'Poppins', sans-serif", fontSize: '0.95rem', color: '#222222', opacity: 0.7, lineHeight: '1.8' },
  shareContainer: { textAlign: 'center', padding: '36px', background: '#e8e1da', borderRadius: '16px' },
  shareTitle: { fontFamily: "'Poltawski Nowy', serif", fontWeight: 'bold', marginBottom: '20px', fontSize: '1.2rem', color: '#222222' },
  shareButtons: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' },
  shareBtn: { padding: '12px 24px', borderRadius: '9999px', color: 'white', textDecoration: 'none', fontFamily: "'Poppins', sans-serif", fontWeight: 'bold', fontSize: '0.9rem' },
  ctaSection: { background: '#e8e1da', textAlign: 'center', padding: '80px 20px',marginBottom:"4rem" },
  ctaSectionTitle: { fontFamily: "'Poltawski Nowy', serif", fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 'bold', color: '#222222', marginBottom: '16px' },
  ctaSectionText: { fontFamily: "'Poppins', sans-serif", fontSize: '1.1rem', color: '#222222', opacity: 0.6, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' },
  ctaButton: { display: 'inline-block', background: '#967d6c', color: 'white', padding: '16px 48px', borderRadius: '9999px', fontFamily: "'Poppins', sans-serif", fontWeight: 'bold', fontSize: '1.1rem', textDecoration: 'none' },
};

export default BlogPost;